import axios, { AxiosInstance } from 'axios';
import { adminConfig } from '../config/admin';
import type { User, LoginSession, AdminAction, AdminStats, UsageSummaryResponse, ModelConfig, ProviderKey, AgentKey, BillingBreakdownResponse, RequestDetail } from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: adminConfig.apiBaseUrl,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for authentication — attach Firebase ID token
    this.api.interceptors.request.use(async (config) => {
      try {
        const { auth } = await import('../config/firebase');
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : undefined;
        if (token) config.headers.Authorization = `Bearer ${token}`;
      } catch {
        // Not signed in or auth isn't initialized yet
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(email: string, password: string): Promise<{ token: string; user: any }> {
    const response = await this.api.post('/auth/admin/login', { email, password });
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/admin/logout');
    localStorage.removeItem('admin_token');
  }

  // Users
  async getUsers(): Promise<User[]> {
    const response = await this.api.get('/admin/users');
    return response.data.data || [];
  }

  async getUser(userId: string): Promise<User> {
    const response = await this.api.get(`/admin/users/${userId}`);
    return response.data.data;
  }

  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    const response = await this.api.patch(`/admin/users/${userId}`, updates);
    return response.data.data;
  }

  async deleteUser(userId: string): Promise<void> {
    await this.api.delete(`/admin/users/${userId}`);
  }

  // Login Sessions
  async getLoginSessions(): Promise<LoginSession[]> {
    const response = await this.api.get('/admin/sessions');
    return response.data.data || [];
  }

  // Admin Actions
  async getAdminActions(): Promise<AdminAction[]> {
    const response = await this.api.get('/admin/actions');
    return response.data.data || [];
  }

  async createAdminAction(action: Omit<AdminAction, 'id' | 'createdAt' | 'adminId' | 'adminEmail'>): Promise<AdminAction> {
    const response = await this.api.post('/admin/actions', action);
    return response.data.data;
  }

  async updateAdminAction(actionId: string, updates: Partial<AdminAction>): Promise<AdminAction> {
    const response = await this.api.patch(`/admin/actions/${actionId}`, updates);
    return response.data.data;
  }

  // Statistics
  async getStats(): Promise<AdminStats> {
    const response = await this.api.get('/admin/stats');
    return response.data.data;
  }

  // Subscription Management
  async promoteUser(userId: string, tier: string, duration: number): Promise<User> {
    const response = await this.api.post(`/admin/users/${userId}/promote`, {
      tier,
      duration
    });
    return response.data.data;
  }

  async issueRefund(userId: string, amount: number, reason: string): Promise<AdminAction> {
    const response = await this.api.post(`/admin/users/${userId}/refund`, {
      amount,
      reason
    });
    return response.data.data;
  }

  async suspendUser(userId: string, reason: string): Promise<User> {
    const response = await this.api.post(`/admin/users/${userId}/suspend`, {
      reason
    });
    return response.data.data;
  }

  async activateUser(userId: string): Promise<User> {
    const response = await this.api.post(`/admin/users/${userId}/activate`);
    return response.data.data;
  }

  // Usage analytics
  async getUsageSummary(days = 30): Promise<UsageSummaryResponse> {
    const response = await this.api.get(`/usage/summary?days=${days}`);
    return response.data.data;
  }

  async getUserUsage(userId: string, days = 30): Promise<UsageSummaryResponse> {
    const response = await this.api.get(`/usage/users/${userId}?days=${days}`);
    return response.data.data;
  }

  async getRequestDetail(requestId: string): Promise<RequestDetail> {
    const response = await this.api.get(`/usage/requests/${requestId}`);
    return response.data.data;
  }

  async getBillingSummary(days = 30): Promise<UsageSummaryResponse> {
    const response = await this.api.get(`/admin/billing/summary?days=${days}`);
    return response.data.data;
  }

  async getBillingBreakdown(days = 30, groupBy = 'model'): Promise<BillingBreakdownResponse> {
    const response = await this.api.get(`/admin/billing/breakdown?days=${days}&groupBy=${groupBy}`);
    return response.data.data;
  }

  async getUserBilling(userId: string, days = 30): Promise<UsageSummaryResponse> {
    const response = await this.api.get(`/admin/billing/users/${userId}?days=${days}`);
    return response.data.data;
  }

  // Model configs
  async listModels(): Promise<ModelConfig[]> {
    const response = await this.api.get('/admin/models');
    return response.data.data ?? [];
  }

  async createModel(data: Partial<ModelConfig>): Promise<ModelConfig> {
    const response = await this.api.post('/admin/models', data);
    return response.data.data;
  }

  async updateModel(id: string, data: Partial<ModelConfig>): Promise<ModelConfig> {
    const response = await this.api.patch(`/admin/models/${id}`, data);
    return response.data.data;
  }

  async deleteModel(id: string): Promise<void> {
    await this.api.delete(`/admin/models/${id}`);
  }

  // Provider keys
  async listProviderKeys(): Promise<ProviderKey[]> {
    const response = await this.api.get('/admin/keys/provider');
    return response.data.data ?? [];
  }

  async createProviderKey(provider: string, rawKey: string): Promise<ProviderKey> {
    const response = await this.api.post('/admin/keys/provider', { provider, rawKey });
    return response.data.data;
  }

  async revokeProviderKey(id: string): Promise<void> {
    await this.api.post(`/admin/keys/provider/${id}/revoke`);
  }

  async rotateProviderKey(id: string, newRawKey: string): Promise<void> {
    await this.api.post(`/admin/keys/provider/${id}/rotate`, { newRawKey });
  }

  // Agent keys
  async listAgentKeys(): Promise<AgentKey[]> {
    const response = await this.api.get('/admin/keys/agent');
    return response.data.data ?? [];
  }

  async createAgentKey(agentId: string, scopes: string[], quotaRpm: number): Promise<{ data: AgentKey; token: string }> {
    const response = await this.api.post('/admin/keys/agent', { agentId, scopes, quotaRpm });
    return response.data;
  }

  async revokeAgentKey(id: string): Promise<void> {
    await this.api.post(`/admin/keys/agent/${id}/revoke`);
  }

  // System config
  async getSystemConfig(): Promise<any> {
    const response = await this.api.get('/admin/system');
    return response.data.data;
  }

  async updateSystemConfig(config: any): Promise<any> {
    const response = await this.api.patch('/admin/system', config);
    return response.data.data;
  }

  // Account (GDPR)
  async exportAccountData(): Promise<any> {
    const response = await this.api.get('/account/export');
    return response.data;
  }

  async deleteAccount(): Promise<any> {
    const response = await this.api.delete('/account');
    return response.data;
  }

  async getPrivacyPreferences(): Promise<any> {
    const response = await this.api.get('/account/privacy');
    return response.data.data;
  }

  async updatePrivacyPreferences(prefs: { telemetryOptIn?: boolean; conversationalInsightOptIn?: boolean }): Promise<any> {
    const response = await this.api.patch('/account/privacy', prefs);
    return response.data.data;
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.api.get('/health');
      return response.status === 200;
    } catch {
      return false;
    }
  }

  async getTelemetryStats(): Promise<unknown> {
    const response = await this.api.get('/admin/telemetry/stats');
    return response.data;
  }

  async getCfConfig(): Promise<{ accountId: string; workerName: string; kvNamespaceId: string; tokenSet: boolean; updatedAt?: string; updatedBy?: string }> {
    const response = await this.api.get('/admin/system/cf-config');
    return response.data.data;
  }

  async saveCfConfig(cfg: { accountId?: string; workerName?: string; kvNamespaceId?: string; apiToken?: string }): Promise<void> {
    await this.api.patch('/admin/system/cf-config', cfg);
  }

  async testCfConnection(): Promise<{ success: boolean; message?: string; error?: string; workerFound?: string }> {
    const response = await this.api.post('/admin/system/cf-config/test', {});
    return response.data;
  }

  async getRuntimeStatus(): Promise<{
    requirements: {
      id: string;
      name: string;
      description: string;
      category: 'backend' | 'cf_worker' | 'keys_models' | 'edge';
      status: 'ok' | 'missing' | 'warning' | 'unknown';
      detail?: string;
      fixHint?: string;
    }[];
    summary: { total: number; ok: number; missing: number; warning: number; unknown: number };
    checkedAt: string;
  }> {
    const response = await this.api.get('/admin/system/runtime-status');
    return response.data.data;
  }

  // Backend runtime config (JWT_SECRET, WORKER_SHARED_SECRET, AEGIS_CLOUD_PROXY_URL)
  async getBackendConfig(): Promise<{ proxyUrl: string; jwtSecretSet: boolean; workerSecretSet: boolean; updatedAt?: string; updatedBy?: string }> {
    const response = await this.api.get('/admin/system/backend-config');
    return response.data.data;
  }

  async saveBackendConfig(data: { proxyUrl?: string; jwtSecret?: string; workerSharedSecret?: string }): Promise<void> {
    await this.api.patch('/admin/system/backend-config', data);
  }

  // CF Worker secrets (names only — values never returned)
  async listCfSecrets(): Promise<{ secrets: string[]; error?: string }> {
    const response = await this.api.get('/admin/system/cf-secrets');
    return response.data.data;
  }

  async pushCfSecret(name: string, value: string): Promise<void> {
    await this.api.put(`/admin/system/cf-secrets/${name}`, { value });
  }
}
export const apiService = new ApiService();